/**
 * Rebrand: inline fabric quick-pick for made-to-order products.
 * Shows the first few fabric swatches from the MTO configurator as circles so customers
 * can pick without opening the drawer; "+N" opens the full fabric drawer.
 * Also aligns button wording with the rebrand ("Add to basket", "Order free swatches").
 * Works alongside assets/mto-configurator.js without modifying it.
 */
const MAX_SWATCHES = 6;

/** @param {any} configurator */
function initQuickPick(configurator) {
  if (configurator.dataset.quickpickReady) return;
  configurator.dataset.quickpickReady = 'true';

  const submit = configurator.querySelector('.mto-submit');
  if (submit && submit.textContent.trim() === 'Add to cart') submit.textContent = 'Add to basket';
  const swatchBtn = configurator.querySelector('.mto-swatch-request-open');
  if (swatchBtn) swatchBtn.textContent = 'Order free swatches';

  const step = configurator.querySelector('.mto-step--fabric');
  const cta = configurator.querySelector('.mto-step__cta[data-step-key="fabric"]');
  const drawer = configurator.querySelector('.mto-drawer[data-step-key="fabric"]');
  if (!step || !cta || !drawer) return;

  /** @type {HTMLInputElement[]} */
  const inputs = Array.from(drawer.querySelectorAll('input[name="mto-fabric-swatch"]'));
  if (inputs.length === 0) return;

  const label = (cta.querySelector('.mto-step__cta-body')?.firstChild?.textContent || 'Fabric:')
    .replace(':', '')
    .trim()
    .toLowerCase();

  const wrap = document.createElement('div');
  wrap.className = 'mto-quickpick';
  const heading = document.createElement('p');
  heading.className = 'mto-quickpick__label';
  heading.textContent = `Choose your ${label}`;
  const nameEl = document.createElement('span');
  nameEl.className = 'mto-quickpick__name';
  heading.append(nameEl);
  const row = document.createElement('div');
  row.className = 'mto-quickpick__row';
  row.setAttribute('role', 'radiogroup');
  row.setAttribute('aria-label', `Choose your ${label}`);
  wrap.append(heading, row);

  const shown = inputs.slice(0, inputs.length > MAX_SWATCHES ? MAX_SWATCHES - 1 : MAX_SWATCHES);
  /** @type {HTMLButtonElement[]} */
  const buttons = [];

  shown.forEach((input) => {
    const card = input.closest('.mto-swatch-card');
    const name = card?.getAttribute('title')?.trim() || input.value;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mto-quickpick__swatch';
    btn.setAttribute('role', 'radio');
    btn.setAttribute('aria-checked', 'false');
    btn.setAttribute('aria-label', name);
    btn.title = name;

    const img = card?.querySelector('.mto-swatch-card__img');
    if (img) {
      const thumb = /** @type {HTMLImageElement} */ (img.cloneNode(true));
      thumb.removeAttribute('class');
      thumb.alt = '';
      thumb.loading = 'eager';
      btn.append(thumb);
    } else {
      const fallback = document.createElement('span');
      fallback.className = 'mto-quickpick__fallback';
      fallback.textContent = name.slice(0, 1);
      btn.append(fallback);
    }

    btn.addEventListener('click', () => {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      // Updates the (hidden) step button label/thumbnail exactly as the drawer's Confirm does.
      if (typeof configurator._confirmAndClose === 'function') configurator._confirmAndClose(drawer);
      sync();
    });

    buttons.push(btn);
    row.append(btn);
  });

  /** @type {HTMLButtonElement | null} */
  let more = null;
  if (inputs.length > shown.length) {
    more = document.createElement('button');
    more.type = 'button';
    more.className = 'mto-quickpick__swatch mto-quickpick__more';
    more.textContent = `+${inputs.length - shown.length}`;
    more.setAttribute('aria-label', `See all ${inputs.length} options`);
    more.setAttribute('aria-haspopup', 'dialog');
    more.addEventListener('click', () => /** @type {HTMLElement} */ (cta).click());
    row.append(more);
  }

  function sync() {
    const checked = inputs.find((i) => i.checked);
    const shownIndex = checked ? shown.indexOf(checked) : -1;
    buttons.forEach((b, i) => b.setAttribute('aria-checked', String(i === shownIndex)));
    if (more) more.setAttribute('aria-checked', String(Boolean(checked) && shownIndex === -1));
    nameEl.textContent = checked?.closest('.mto-swatch-card')?.getAttribute('title')?.trim() ?? '';
  }

  drawer.addEventListener('change', sync);
  drawer.addEventListener('close', sync);
  sync();

  step.prepend(wrap);
  step.classList.add('mto-step--quickpick');
}

/**
 * The basket button stays full colour while options are incomplete, so instead of
 * silently doing nothing, a click opens the first step the customer still needs.
 * @param {any} configurator
 */
function guardSubmit(configurator) {
  const submit = configurator.querySelector('.mto-submit');
  const form = configurator.querySelector('.mto-form');
  if (!submit || !form) return;

  const openNextStep = () => {
    const steps = [
      ['fabric', '.mto-prop-fabric-swatch'],
      ['cushion', '.mto-prop-cushion-swatch'],
      ['wood', '.mto-prop-wood-finish'],
    ];
    for (const [key, sel] of steps) {
      const input = configurator.querySelector(sel);
      if (input && !input.value) {
        configurator.querySelector(`.mto-step__cta[data-step-key="${key}"]`)?.click();
        return;
      }
    }
  };

  const block = (event) => {
    if (submit.getAttribute('aria-disabled') !== 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openNextStep();
  };

  submit.addEventListener('click', block, true);
  configurator.addEventListener('submit', block, true);
}

customElements.whenDefined('mto-configurator').then(() => {
  document.querySelectorAll('mto-configurator').forEach((el) => {
    initQuickPick(el);
    guardSubmit(el);
  });
});